/**
 * Load an uploaded drawing into a payload that's directly displayable as an
 * `<img>` plus its intrinsic pixel size.
 *
 * For raster sources we use the original URL — the browser handles
 * decoding and downscaling much better than canvas.drawImage.
 *
 * For PDFs we render page 1 with pdf.js to a high-resolution canvas and
 * convert it to a PNG data URL. The downstream `<img>` then gets the
 * same hardware-accelerated downscale path as a normal image.
 */
export interface DrawingPayload {
  imageUrl: string;
  naturalWidth: number;
  naturalHeight: number;
}

import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

export async function loadDrawingFromUrl(
  url: string,
  mime: string,
): Promise<DrawingPayload> {
  if (mime === "application/pdf") {
    return renderPdfToImage(url);
  }
  return loadImageAsPayload(url);
}

export async function loadDrawingFromFile(file: File): Promise<DrawingPayload> {
  if (file.type === "application/pdf") {
    const url = URL.createObjectURL(file);
    try {
      return await renderPdfToImage(url);
    } finally {
      URL.revokeObjectURL(url);
    }
  }
  // For images keep the object URL alive — the caller displays it via <img>.
  const objectUrl = URL.createObjectURL(file);
  return loadImageAsPayload(objectUrl);
}

/**
 * Convert a loaded drawing payload to base64-encoded PNG bytes (no
 * `data:` prefix), suitable for posting to `/api/drawing/auto-analyze`.
 *
 * For PDFs the payload's `imageUrl` is already a data URL produced by
 * pdfjs; we just strip the prefix. For raster sources the URL is a
 * blob/http URL — fetch via `<img>`, draw to canvas, export as PNG.
 */
export async function payloadToPngBase64(
  payload: DrawingPayload,
): Promise<string> {
  const url = payload.imageUrl;
  const PREFIX = "data:image/png;base64,";
  if (url.startsWith(PREFIX)) return url.slice(PREFIX.length);
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("Failed to read drawing image"));
    i.src = url;
  });
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth || payload.naturalWidth;
  canvas.height = img.naturalHeight || payload.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");
  ctx.drawImage(img, 0, 0);
  return canvas.toDataURL("image/png").slice(PREFIX.length);
}

async function loadImageAsPayload(url: string): Promise<DrawingPayload> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    i.src = url;
  });
  return {
    imageUrl: url,
    naturalWidth: img.naturalWidth,
    naturalHeight: img.naturalHeight,
  };
}

async function renderPdfToImage(url: string): Promise<DrawingPayload> {
  const pdfjs = await import("pdfjs-dist");
  pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

  const loadingTask = pdfjs.getDocument({ url });
  const pdf = await loadingTask.promise;
  const page = await pdf.getPage(1);

  // Render at a generous resolution so the destination <img> always
  // downscales (browsers' downscale is much better than upscale and avoids
  // moiré on technical drawings).
  const baseViewport = page.getViewport({ scale: 1 });
  const targetLong = 4500;
  const longEdge = Math.max(baseViewport.width, baseViewport.height);
  const scale = targetLong / longEdge;
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get 2d context for PDF render");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  // No `canvas` key: that is a pdfjs-dist v5 render parameter and the
  // pinned v4 RenderParameters type rejects it (this was failing
  // `tsc --noEmit` on main). v4 renders from canvasContext alone.
  await page.render({ canvasContext: ctx, viewport }).promise;
  return {
    imageUrl: canvas.toDataURL("image/png"),
    naturalWidth: canvas.width,
    naturalHeight: canvas.height,
  };
}
